// src/hooks/use-keyboard-height.ts
// Shared keyboard height hook — works on iOS native (Capacitor plugin) and web (visualViewport)

import { useEffect, useState } from 'react';
import { isIos } from '../platform';

export function useKeyboardHeight() {
  const [height, setHeight] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // iOS native: use Capacitor Keyboard plugin events
    if (isIos()) {
      const cleanups: (() => void)[] = [];

      (async () => {
        try {
          const { Keyboard } = await import('@capacitor/keyboard');

          const showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
            setHeight(info.keyboardHeight);
            setOpen(true);
          });
          const hideListener = await Keyboard.addListener('keyboardWillHide', () => {
            setHeight(0);
            setOpen(false);
          });

          cleanups.push(
            () => showListener.remove(),
            () => hideListener.remove(),
          );
        } catch {
          // Plugin not available — no-op
        }
      })();

      return () => { cleanups.forEach(fn => fn()); };
    }

    // Web / Android: use visualViewport API
    const vv = window.visualViewport;
    if (!vv) return;

    const initialHeight = vv.height;

    const measure = () => {
      const layoutH = window.innerHeight;
      const vvH = vv.height ?? layoutH;
      const offsetTop = vv.offsetTop ?? 0;
      const inset = Math.max(0, Math.round(layoutH - vvH - offsetTop));
      setHeight(inset);

      const shrunk = initialHeight - vvH;
      const tag = document.activeElement?.tagName?.toLowerCase() ?? '';
      const inputFocused = tag === 'textarea' || tag === 'input' ||
        document.activeElement?.getAttribute('contenteditable') === 'true';

      setOpen(inset > 0 || shrunk > 100 || inputFocused);
    };

    measure();
    vv.addEventListener('resize', measure);
    vv.addEventListener('scroll', measure);
    window.addEventListener('orientationchange', measure);
    window.addEventListener('resize', measure);
    document.addEventListener('focusin', measure);
    document.addEventListener('focusout', measure);

    return () => {
      vv.removeEventListener('resize', measure);
      vv.removeEventListener('scroll', measure);
      window.removeEventListener('orientationchange', measure);
      window.removeEventListener('resize', measure);
      document.removeEventListener('focusin', measure);
      document.removeEventListener('focusout', measure);
    };
  }, []);

  return { height, open };
}
